<script setup lang="ts">
import { HgIcon, HgSupportedLanguage } from '@smg-real-estate/domus';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import AgencyAgents from '@/components/AgencyAgents/AgencyAgents.vue';
import AgencyAgentsStack from '@/components/AgencyAgents/AgencyAgentsStack.vue';
import AgencyPartnerBadge from '@/components/AgencyCard/AgencyPartnerBadge.vue';
import { sanitizeInput } from '@/helpers/String';
import { generateAgencyProfilePagePath } from '@/helpers/Url/Url';
import type { Agent, PartnerBadgeTier } from '@/types';

defineEmits(['agencyCardClick']);

export interface AgencyCardProps {
  customerId: string;
  profilePageUrlKeyWord: string;
  agencyLogo: string;
  agencyName: string;
  postalCode?: string;
  city?: string;
  language: HgSupportedLanguage;
  agents: Agent[];
  propertiesForRentCount: number;
  propertiesForBuyCount: number;
  description?: string;
  stackHiddenAgents?: boolean;
  partnerBadgeTier?: PartnerBadgeTier;
}

const {
  customerId,
  profilePageUrlKeyWord,
  agencyLogo,
  agencyName,
  postalCode = '',
  city = '',
  language,
  agents,
  description = '',
  stackHiddenAgents = false,
  partnerBadgeTier,
} = defineProps<AgencyCardProps>();

const imageError = ref(false);
const onImageLoadFailure = () => {
  imageError.value = true;
};

const agencyDescription = sanitizeInput(description).trim();
const agentsWithImage = computed(() => agents.filter((agent) => agent.avatarUrl));

const { t } = useI18n();
</script>

<template>
  <a
    :class="$style.agencyCardWrapper"
    :href="generateAgencyProfilePagePath(language, profilePageUrlKeyWord, customerId)"
    @click="$emit('agencyCardClick')"
  >
    <div :class="$style.agencyCard">
      <div :class="$style.agencyBasicInformation">
        <div :class="$style.agencyLogoWrapper">
          <div :class="$style.agencyLogo">
            <img
              v-if="agencyLogo && !imageError"
              loading="lazy"
              :alt="agencyName"
              :src="agencyLogo"
              @error="onImageLoadFailure"
            />
            <HgIcon v-else size="large" icon="Apartment" :themeClass="$style.agencyCardIcon" />
          </div>
        </div>
        <div :class="$style.agencyDetails" data-test="agency-details">
          <div :class="$style.agencyContact">
            <div :class="$style.agencyName" :title="agencyName" data-test="agency-name">
              {{ agencyName }}
            </div>
            <div :class="$style.agencyAddress" data-test="agency-address">
              <HgIcon v-if="city" icon="Pin" size="large" :themeClass="$style.agencyCardIcon" />
              <span>{{ postalCode }} {{ city }}</span>
            </div>
          </div>
          <div :class="$style.agencyProperties">
            <div :class="$style.agencyPropertiesRow">
              <HgIcon
                icon="House"
                size="large"
                :class="$style.agencyPropertyIcon"
                :themeClass="$style.agencyCardIcon"
              />
              <div :class="$style.agencyProperty">
                <div :class="$style.saleProperties" data-test="sale-properties">
                  {{ propertiesForBuyCount }}
                </div>
                <p>{{ t('agencyList.saleProperties') }}</p>
              </div>
              <div :class="$style.agencyProperty">
                <div :class="$style.rentProperties" data-test="rent-properties">
                  {{ propertiesForRentCount }}
                </div>
                <p>{{ t('agencyList.rentProperties') }}</p>
              </div>
            </div>
          </div>
        </div>
        <div v-if="partnerBadgeTier" :class="$style.agencyPartnerBadge">
          <AgencyPartnerBadge :partnerBadgeTier="partnerBadgeTier" />
        </div>
      </div>
      <div v-if="agencyDescription">
        <div :class="$style.agencySubHeadline" data-test="agency-sub-headline">
          {{ t('agencyList.aboutUs') }}
        </div>
        <p :class="$style.agencyDescription" data-test="agency-description">
          {{ agencyDescription }}
        </p>
      </div>
      <AgencyAgentsStack v-if="stackHiddenAgents && agents.length" :agents="agents" />
      <AgencyAgents v-else-if="agentsWithImage.length" :agents="agentsWithImage" />
    </div>
  </a>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.agencyCardIcon {
  @include color-icon($color-grey-70);
}

.agencyCardWrapper {
  display: block;
  border: 1px solid $color-grey-50;
  border-radius: 8px;

  &:hover,
  &:visited,
  &:visited,
  &:active {
    text-decoration: none;
  }

  .agencyCard {
    padding: $space-200;

    @media (max-width: $viewport-sm-min) {
      padding: $space-100;
    }

    .agencyBasicInformation {
      --columns-per-row: 1;

      position: relative;
      display: grid;
      grid-template-columns: repeat(var(--columns-per-row), auto);
      gap: $space-100;
      justify-content: flex-start;

      @media (min-width: $viewport-sm-min) {
        grid-template-columns: $space-1000 1fr $space-400;
        gap: $space-150;
        justify-content: space-evenly;

        --columns-per-row: 3;
      }

      @media (min-width: $viewport-md-min) {
        grid-template-columns: $space-1000 1fr $space-500;
      }
    }

    .agencyPartnerBadge {
      position: absolute;
      top: 0;
      right: 0;

      @media (min-width: $viewport-sm-min) {
        position: static;
        grid-row: 1 / span 2;
        grid-column: 3;
      }

      @media (min-width: $viewport-md-min) {
        grid-row: 1;
        grid-column: 3;
      }
    }

    .agencyLogoWrapper {
      display: flex;
      justify-content: flex-start;
      width: 160px;

      .agencyLogo {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 142px;
        height: 82px;
        padding: 8px;
        border: 1px solid $color-grey-50;
        border-radius: 4px;

        img {
          max-width: 100%;
          height: auto;
          max-height: 82px;
        }
      }
    }

    .saleProperties,
    .rentProperties,
    .agencySubHeadline,
    .agencyName {
      display: -webkit-box;
      margin-top: $space-100;
      margin-bottom: $space-50;
      overflow: hidden;
      font-size: 1.375rem;
      font-weight: 700;
      color: $color-black;
      -webkit-line-clamp: 1;
      word-break: break-all;
      -webkit-box-orient: vertical;

      @media (max-width: $viewport-sm-min) {
        margin-top: 0;
      }
    }

    .agencySubHeadline {
      margin-top: $space-200;
      margin-bottom: 0;

      @include heading-4;

      @media (max-width: $viewport-sm-min) {
        margin-top: $space-50;
      }
    }

    .agencyName {
      margin-top: 0;
    }

    .agencyAddress {
      display: flex;
      align-items: center;

      span {
        margin-left: $space-25;
        color: $color-grey-90;

        @include body-2;
      }
    }

    .agencyProperties {
      margin-top: $space-50;

      .agencyPropertyIcon {
        margin-right: $space-50;
      }

      .agencyPropertiesRow {
        display: flex;
        align-items: center;

        .agencyProperty {
          display: flex;
          gap: $space-25;
          align-items: center;
          padding: 0 $space-75;
          color: $color-grey-90;

          @media (max-width: $viewport-sm-min) {
            margin-top: $space-50;
          }

          &:first-of-type {
            padding-left: 0;
          }

          &:last-of-type {
            border-left: 1px solid $color-grey-50;
          }

          .saleProperties,
          .rentProperties {
            flex-shrink: 0;
          }

          .saleProperties,
          .rentProperties,
          p {
            margin: 0;

            @include body-2;
          }
        }
      }
    }

    .agencyDescription {
      display: -webkit-box;
      margin: 0;
      overflow: hidden;
      color: $color-grey-90;
      -webkit-line-clamp: 4;
      -webkit-box-orient: vertical;
    }
  }
}
</style>
